import React, { useCallback, useEffect, useState } from 'react';
import { sb } from '../../supabase';

// Requests whose translation needs a human, with a retry button.
//
// ── WHY THIS IS ON THE DASHBOARD ───────────────────────────────────────────
// `requests.translation_status` is the monitor for security rule 3: a row
// SITTING at 'pending' means the translate-request edge function never
// reported back. Security rule 3 exists because cron.job_run_details said
// "succeeded" 24 times a day for 25 days while the function 401'd every call.
// A monitor nobody looks at repeats that failure exactly, so this lives on the
// page an admin lands on rather than inside a request detail screen you would
// only open if you already suspected something.
//
// ── THREE SEVERITIES, NOT ONE BADGE ────────────────────────────────────────
// admin_translation_health() separates them and so does this:
//   BLOCKED   no usable English title. Supplier WhatsApp sends and public
//             link minting are refused RIGHT NOW by
//             request_translation_is_clean(). Urgent.
//   STALE     pending for over 5 minutes -- the call never landed. Nothing is
//             blocked yet only because the raw English happens to be usable.
//   DEGRADED  English is fine, the Chinese is a copy of it. A Chinese factory
//             reads English. Advisory, never urgent.
// Collapsing these into one "needs attention" count would bury the blocked
// rows among the merely imperfect, which is how a list stops being read.
//
// Renders NOTHING when the list is empty. An always-present panel showing
// "0 problems" trains people to skip the region it occupies.

const SEV = {
  blocked: { rank: 0, bg: 'rgba(192,57,43,.10)', fg: 'var(--ac-danger, #c0392b)',
    ar: 'موقوف', en: 'BLOCKED' },
  stale: { rank: 1, bg: 'rgba(230,126,34,.12)', fg: '#b9770e',
    ar: 'متأخر', en: 'STALE' },
  degraded: { rank: 2, bg: 'rgba(0,0,0,.06)', fg: 'var(--ac-muted)',
    ar: 'ناقص', en: 'DEGRADED' },
};

// Codes come from request_translation_report(). Spelled out rather than shown
// raw: the person reading this is deciding whether to chase a trader, and
// TITLE_EN_IS_ARABIC is not a sentence.
const REASON = {
  TITLE_EN_MISSING: { ar: 'لا يوجد عنوان إنجليزي', en: 'No English title' },
  TITLE_EN_IS_ARABIC: { ar: 'العنوان الإنجليزي مكتوب بالعربية', en: 'The English title is in Arabic' },
  TITLE_ZH_MISSING: { ar: 'لا يوجد عنوان صيني', en: 'No Chinese title' },
  TITLE_ZH_IS_ARABIC: { ar: 'العنوان الصيني مكتوب بالعربية', en: 'The Chinese title is in Arabic' },
  DESCRIPTION_EN_IS_ARABIC: { ar: 'الوصف الإنجليزي مكتوب بالعربية', en: 'The English description is in Arabic' },
  TITLE_ZH_IS_A_COPY_OF_EN: { ar: 'العنوان الصيني نسخة من الإنجليزي', en: 'The Chinese title is a copy of the English' },
};

export default function TranslationHealthPanel({ isAr = true }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  // (d): correct the English title from the row itself. A fix that takes
  // three screens is a fix that waits, and the row holding up a paid send is
  // the one that must not wait.
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    const { data, error } = await sb.rpc('admin_translation_health');
    // Security rule 4 applied to our own backend: a swallowed read here shows
    // an empty panel, which reads as "nothing wrong" -- the single worst thing
    // this component could say.
    if (error) {
      console.error('[TranslationHealthPanel] read failed:', error.code || '', error.message || error);
      setErr(isAr ? 'تعذّرت قراءة حالة الترجمة.' : 'Could not read translation health.');
      return;
    }
    setErr('');
    setRows(data || []);
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const saveTitle = async (id) => {
    const value = draft.trim();
    if (!value) return;
    setBusyId(id);
    setNote('');
    const { error } = await sb.rpc('admin_set_request_title_en', {
      p_request_id: id, p_title_en: value,
    });
    setBusyId(null);
    if (error) {
      console.error('[TranslationHealthPanel] title save failed:', error.code || '', error.message || error);
      // The RPC's own named codes, spelled out. TITLE_EN_IS_ARABIC is the one
      // an admin will actually hit -- pasting the Arabic in by mistake is the
      // obvious thing to do when the Arabic is what you are looking at.
      const msg = String(error.message || '');
      setNote(
        msg.includes('TITLE_EN_IS_ARABIC')
          ? (isAr ? 'العنوان الإنجليزي لا يمكن أن يكون بالعربية.' : 'The English title cannot be in Arabic.')
          : msg.includes('TITLE_EN_EMPTY')
            ? (isAr ? 'اكتب عنواناً.' : 'Enter a title.')
            : (isAr ? 'تعذّر الحفظ.' : 'Could not save.'),
      );
      return;
    }
    setEditId(null);
    setDraft('');
    // Unlike retry, this one IS synchronous -- the row may have left the list
    // entirely, so reload rather than telling them to come back later.
    await load();
  };

  const retry = async (id) => {
    setBusyId(id);
    setNote('');
    const { error } = await sb.rpc('admin_retry_request_translation', { p_request_id: id });
    setBusyId(null);
    if (error) {
      console.error('[TranslationHealthPanel] retry failed:', error.code || '', error.message || error);
      setNote(isAr ? 'تعذّرت إعادة المحاولة.' : 'Retry failed.');
      return;
    }
    // The edge function is called asynchronously through pg_net, so the row
    // will not have changed yet. Say that plainly instead of reloading into an
    // unchanged list, which looks like the button did nothing.
    setNote(isAr
      ? 'أُعيد الإرسال للترجمة. حدّث بعد دقيقة لرؤية النتيجة.'
      : 'Sent for re-translation. Refresh in a minute to see the result.');
  };

  if (!err && rows.length === 0) return null;

  const wrap = { marginBottom: 20 };
  const head = { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 };

  return (
    <div className="ac-card" style={{ padding: 18, ...wrap }}>
      <div style={head}>
        <strong style={{ fontSize: 14 }}>
          {isAr ? 'ترجمة الطلبات' : 'Request translation'}
        </strong>
        <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>
          {isAr
            ? 'الطلب الموقوف لا تُرسل نصوصه إلى المورّدين ولا يُصدَر له رابط عام.'
            : 'A blocked request cannot be sent to suppliers and cannot have a public link minted.'}
        </span>
      </div>

      {err && <p style={{ fontSize: 13, color: 'var(--ac-danger, #c0392b)', margin: '0 0 10px' }}>{err}</p>}
      {note && <p style={{ fontSize: 13, color: 'var(--ac-muted)', margin: '0 0 10px' }}>{note}</p>}

      {rows.map((r) => {
        const sev = SEV[r.severity] || SEV.degraded;
        const reason = REASON[r.reason];
        return (
          <div key={r.request_id} style={{
            display: 'flex', gap: 12, padding: '10px 0',
            // Top-aligned rather than centred, so an open editor pushes the
            // row down instead of shifting the badge and buttons off-centre.
            alignItems: editId === r.request_id ? 'flex-start' : 'center',
            borderBottom: '1px solid var(--ac-line, rgba(0,0,0,.06))',
          }}>
            <span style={{
              fontSize: 10, letterSpacing: 1, textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
              background: sev.bg, color: sev.fg,
            }}>
              {isAr ? sev.ar : sev.en}
            </span>

            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14 }}>
                {r.request_ref || r.request_id.slice(0, 8)}
                {/* The title is shown so an admin can judge and fix it here
                    rather than opening the request to find out what is wrong. */}
                {r.title_en ? <span style={{ color: 'var(--ac-muted)' }}> — {r.title_en}</span> : null}
              </span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--ac-muted)' }}>
                {reason ? (isAr ? reason.ar : reason.en) : r.reason}
                {r.severity === 'stale' && r.minutes_waiting != null
                  ? (isAr ? ` · بانتظار ${r.minutes_waiting} دقيقة` : ` · waiting ${r.minutes_waiting} min`)
                  : ''}
              </span>

              {editId === r.request_id && (
                <span style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(r.request_id); if (e.key === 'Escape') setEditId(null); }}
                    autoFocus
                    dir="ltr"
                    placeholder={isAr ? 'العنوان بالإنجليزية' : 'English title'}
                    style={{
                      flex: 1, padding: '7px 10px', fontSize: 13, borderRadius: 6,
                      border: '1px solid var(--ac-line, rgba(0,0,0,.18))',
                    }}
                  />
                  <button className="ac-btn ac-btn-sm ac-btn-primary"
                          onClick={() => saveTitle(r.request_id)}
                          disabled={busyId === r.request_id || !draft.trim()}>
                    {isAr ? 'احفظ' : 'Save'}
                  </button>
                  <button className="ac-btn ac-btn-sm" onClick={() => setEditId(null)}>
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </button>
                </span>
              )}
            </span>

            <button
              className="ac-btn ac-btn-sm"
              onClick={() => { setEditId(r.request_id); setDraft(r.title_en || ''); setNote(''); }}
              disabled={busyId === r.request_id}
            >
              {isAr ? 'عدّل العنوان' : 'Edit title'}
            </button>
            <button
              className="ac-btn ac-btn-sm"
              onClick={() => retry(r.request_id)}
              disabled={busyId === r.request_id}
            >
              {busyId === r.request_id ? '…' : (isAr ? 'أعد المحاولة' : 'Retry')}
            </button>
          </div>
        );
      })}
    </div>
  );
}
